import React, { useState, useEffect } from 'react';
import { collection, addDoc, deleteDoc, doc, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Save } from '../types';
import { Plus, X, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';

interface SavesListProps {
  gameId: string;
  onSaveAdded?: () => void;
  onSaveDeleted?: () => void;
}

export default function SavesList({ gameId, onSaveAdded, onSaveDeleted }: SavesListProps) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [saveToDelete, setSaveToDelete] = useState<Save | null>(null);
  const [saves, setSaves] = useState<Save[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    situationDescription: ''
  });

  useEffect(() => {
    const fetchSaves = async () => {
      try {
        const savesQuery = query(
          collection(db, 'saves'),
          where('gameId', '==', gameId),
          orderBy('createdAt', 'desc')
        );
        
        const savesSnapshot = await getDocs(savesQuery);
        const savesData = savesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt.toDate()
        })) as Save[];

        setSaves(savesData);
      } catch (err) {
        console.error('Error fetching saves:', err);
        setError('Failed to load saves');
      }
    };

    fetchSaves();
  }, [gameId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError('');

    try {
      const saveData = {
        gameId,
        title: formData.title,
        situationDescription: formData.situationDescription,
        createdAt: new Date()
      };

      await addDoc(collection(db, 'saves'), saveData);
      
      // Refresh saves list
      const savesQuery = query(
        collection(db, 'saves'),
        where('gameId', '==', gameId),
        orderBy('createdAt', 'desc')
      );
      
      const savesSnapshot = await getDocs(savesQuery);
      const savesData = savesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate()
      })) as Save[];

      setSaves(savesData);
      setFormData({
        title: '',
        situationDescription: ''
      });
      setShowForm(false);
      onSaveAdded?.();
    } catch (err) {
      console.error('Error adding save:', err);
      setError('Failed to add save');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (saveId: string) => {
    try {
      await deleteDoc(doc(db, 'saves', saveId));
      
      // Refresh saves list
      const savesQuery = query(
        collection(db, 'saves'),
        where('gameId', '==', gameId),
        orderBy('createdAt', 'desc')
      );
      
      const savesSnapshot = await getDocs(savesQuery);
      const savesData = savesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate()
      })) as Save[];

      setSaves(savesData);
      onSaveDeleted?.();
      setSaveToDelete(null);
    } catch (err) {
      console.error('Error deleting save:', err);
      setError('Failed to delete save');
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-lg font-medium text-gray-900"
        >
          {expanded ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
          {t('savesList.title')}
        </button>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="text-[#1b3968] hover:text-[#1b3968]/80 flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            {t('savesList.addSave')}
          </button>
        )}
      </div>

      {expanded && (
        <div className="space-y-4">
          <p className="text-gray-600 text-sm mb-4">
            {t('savesList.description')}
          </p>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {showForm && (
            <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('savesList.titleLabel')}
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]"
                  placeholder={t('savesList.titlePlaceholder')}
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('savesList.descriptionLabel')}
                </label>
                <textarea
                  value={formData.situationDescription}
                  onChange={(e) => setFormData({ ...formData, situationDescription: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]"
                  rows={3}
                  placeholder={t('savesList.descriptionPlaceholder')}
                  required
                  disabled={loading}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setFormData({
                      title: '',
                      situationDescription: ''
                    });
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  disabled={loading}
                >
                  {t('savesList.cancel')}
                </button>
                <button
                  type="submit"
                  className="bg-[#1b3968] text-white px-4 py-2 rounded-md hover:bg-[#1b3968]/90 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? t('savesList.adding') : t('savesList.addSaveButton')}
                </button>
              </div>
            </form>
          )}

          {saves.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t('savesList.noSavesYet')}
            </div>
          ) : (
            <div className="space-y-4">
              {saves.map((save) => (
                <div key={save.id} className="bg-gray-50 rounded-lg p-4 relative">
                  <button
                    onClick={() => setSaveToDelete(save)}
                    className="absolute top-4 right-4 text-gray-400 hover:text-red-500"
                    title={t('savesList.deleteSave')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <h4 className="font-medium text-gray-900 mb-2">{save.title}</h4>
                  <p className="text-gray-600">{save.situationDescription}</p>
                  <div className="mt-2 text-sm text-gray-500">
                    {save.createdAt.toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {saveToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">{t('savesList.deleteSaveTitle')}</h2>
            <p className="text-gray-600 mb-6">
              {t('savesList.deleteSaveConfirmation')}
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setSaveToDelete(null)}
                className="text-gray-600 hover:text-gray-900"
              >
                {t('savesList.cancel')}
              </button>
              <button
                onClick={() => handleDelete(saveToDelete.id)}
                className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
              >
                {t('savesList.deleteSave')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}